import { useState, useEffect, useRef } from 'react';
import { IconButton, Paper, Typography, Box, TextField, Avatar } from '@mui/material'; 
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import CloseIcon from '@mui/icons-material/Close';
import colors from '../colors';
import { ReactComponent as IconEasyPayLogo } from '../../icons/epIconNew.svg';
import { useDispatch, useSelector } from 'react-redux';
import { selectUserInfo } from '../../features/userInfo/userInfoSlice';
import { sendGeneralRequest } from '../../features/generalRequest';
import { addBotMessage, selectGlobalDataState, setBotMessages, setBotSessionId } from '../../features/global/globalDataSlice';

const Chatbot = () => {
    const userInfo = useSelector(selectUserInfo);
    const globalSliceState = useSelector(selectGlobalDataState);
    const dispatch = useDispatch();
    const messagesEndRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const messages = globalSliceState.botMessages || [];
    const sessionId = globalSliceState.botSessionId || '';

    useEffect(() => {
        if (messages.length == 0)
            dispatch(setBotMessages([{ from: 'bot', text: `Përshendetje ${userInfo.name}! Si mund të ndihmoj?` }]));
    }, []);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isTyping]);

    useEffect(() => {
        if (open) {
            setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        }
    }, [open]);

    const handleSend = () => {
        if (!message.trim()) return;
        dispatch(addBotMessage({ from: 'user', text: message }));

        const sentMessage = message;
        setMessage('');
        setIsTyping(true);

        dispatch(sendGeneralRequest({
            request: {
                token: globalSliceState.token,
                question: sentMessage,
                sessionId: sessionId,
            },
            baseUrlKey: 'services',
            endpoint: 'Chatbot/SendMessage'
        })).then((response) => {
            setIsTyping(false);
            const resp = response.payload?.response;

            if (resp) {
                dispatch(setBotSessionId(resp.sessionId));
                dispatch(addBotMessage({ from: 'bot', text: resp.answer || 'Nuk mora përgjigje.' }));
            } else {
                dispatch(addBotMessage({ from: 'bot', text: 'Gabim gjatë marrjes së përgjigjes.' }));
            }
        });
    };

    return (
        <>
            {/* Butoni */}
            {!open && (
                <IconButton
                    onClick={() => setOpen(true)}
                    sx={{
                        position: 'fixed',
                        bottom: 25,
                        right: 25,
                        width: 50,
                        height: 50,
                        borderRadius: '50%',
                        background: 'linear-gradient(180deg, #F9A23B 0%, #FFD580 100%)',
                        boxShadow: '0px 4px 12px rgba(249, 162, 59, 0.5)',
                        color: '#6B3B2A',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            transform: 'scale(1.05)',
                            boxShadow: '0px 6px 16px rgba(249, 162, 59, 0.6)',
                        },
                    }}
                >
                    <ChatBubbleIcon sx={{ fontSize: 22 }} />
                </IconButton>
            )}

            {/* Chat */}
            {open && (
                <Paper
                    elevation={5}
                    sx={{
                        position: 'fixed',
                        bottom: 25,
                        right: 25,
                        width: 360,
                        height: 470,
                        borderRadius: '15px',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        animation: 'slideUp 0.3s ease',
                        zIndex: 2000,
                        '@keyframes slideUp': {
                            from: { transform: 'translateY(20px)', opacity: 0 },
                            to: { transform: 'translateY(0)', opacity: 1 },
                        },
                    }}
                >
                    {/* Header */}
                    <Box
                        sx={{
                            background: colors.gradientOrange,
                            color: 'white',
                            p: 1.5,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderRadius: '8px 0 0 0',
                        }}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <IconEasyPayLogo style={{ width: 60, height: 20, filter: 'brightness(0) invert(1)' }} />
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.25rem', lineHeight: 1.2 }}>
                                Mirësevini
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <Avatar sx={{ backgroundColor: colors.darkBlue, color: colors.white }}>
                                {userInfo?.name
                                    ?.split(' ')
                                    .map((w) => w[0]?.toUpperCase())
                                    .join('')
                                    .slice(0, 2) || 'U'}
                            </Avatar>
                            <IconButton onClick={() => setOpen(false)} sx={{ color: 'white', mt: -2, mr: -2 }}>
                                <CloseIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Mesazhet */}
                    <Box
                        sx={{
                            flex: 1,
                            p: 1.5,
                            overflowY: 'auto',
                            backgroundColor: 'white',
                        }}
                    >
                        {messages.map((msg, i) => (
                            <Box
                                key={i}
                                sx={{
                                    display: 'flex',
                                    justifyContent: msg.from === 'user' ? 'flex-end' : 'flex-start',
                                    mb: 1.5,
                                }}
                            >
                                {msg.from === 'bot' && (
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                                        <Avatar
                                            sx={{
                                                backgroundColor: colors.orange,
                                                width: 32,
                                                height: 32,
                                                mt: 0.5,
                                                p: 0.5,
                                            }}
                                        >
                                            <IconEasyPayLogo style={{ width: 22, height: 22, filter: 'brightness(0) invert(1)' }} />
                                        </Avatar>
                                        <Typography
                                            sx={{
                                                backgroundColor: '#F3F4F6',
                                                color: colors.darkBlue,
                                                borderRadius: 2,
                                                p: 1,
                                                fontSize: '0.9rem',
                                                mt: 0.5,
                                                maxWidth: '75%',
                                                textAlign: 'left'
                                            }}
                                        >
                                            {msg.text}
                                        </Typography>
                                    </Box>
                                )}
                                {msg.from === 'user' && (
                                    <Typography
                                        sx={{
                                            backgroundColor: colors.orange,
                                            color: 'white',
                                            borderRadius: 2,
                                            p: 1,
                                            fontSize: '0.9rem',
                                            maxWidth: '75%',
                                            textAlign: 'left',
                                        }}
                                    >
                                        {msg.text}
                                    </Typography>
                                )}
                            </Box>
                        ))}

                        {isTyping && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar
                                    sx={{
                                        backgroundColor: colors.orange,
                                        width: 32,
                                        height: 32,
                                        p: 0.5,
                                    }}
                                >
                                    <IconEasyPayLogo
                                        style={{ width: 22, height: 22, filter: 'brightness(0) invert(1)' }}
                                    />
                                </Avatar>
                                <Box
                                    sx={{
                                        backgroundColor: '#F3F4F6',
                                        borderRadius: 2,
                                        p: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        width: 50,
                                        height: 25,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 6,
                                            height: 6,
                                            borderRadius: '50%',
                                            backgroundColor: colors.darkBlue,
                                            animation: 'typingDot 1.4s infinite ease-in-out both',
                                            '@keyframes typingDot': {
                                                '0%, 80%, 100%': { transform: 'scale(0)' },
                                                '40%': { transform: 'scale(1)' },
                                            },
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            width: 6,
                                            height: 6,
                                            borderRadius: '50%',
                                            backgroundColor: colors.darkBlue,
                                            animation: 'typingDot 1.4s infinite ease-in-out both',
                                            animationDelay: '0.2s',
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            width: 6,
                                            height: 6,
                                            borderRadius: '50%',
                                            backgroundColor: colors.darkBlue,
                                            animation: 'typingDot 1.4s infinite ease-in-out both',
                                            animationDelay: '0.4s',
                                        }}
                                    />
                                </Box>
                            </Box>
                        )}
                        <div ref={messagesEndRef} />
                    </Box>

                    {/* Input */}
                    <Box
                        sx={{
                            p: 1.5,
                            mx: 1,
                            borderTop: `2px solid ${colors.orange}`,
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: 'white',
                        }}
                    >
                        <TextField
                            size="small"
                            variant="standard"
                            placeholder="Shkruaj mesazhin..."
                            fullWidth
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    fontSize: '0.9rem',
                                    color: colors.darkBlue,
                                },
                            }}
                        />
                    </Box>
                </Paper>
            )}
        </>
    );
};

export default Chatbot;